import { NavLink } from 'react-router-dom';
import Icon from './Icon';
import BrandLogo from './BrandLogo';
import { initialsOf, useAccount } from '../auth/AccountContext';

interface RailItem {
  to: string;
  label: string;
  icon: string;
}

/** Four Android top-level destinations (DESIGN.md §Layout). */
const RAIL_ITEMS: RailItem[] = [
  { to: '/app/home', label: 'Home', icon: 'home' },
  { to: '/app/history', label: 'History', icon: 'history' },
  { to: '/app/snippets', label: 'Snippets', icon: 'content_copy' },
  { to: '/app/settings', label: 'Settings', icon: 'settings' },
];

function railClass(active: boolean): string {
  return [
    'flex min-h-11 w-full flex-col items-center justify-center rounded-xl p-3 transition-colors group relative',
    active
      ? 'bg-primary text-on-primary font-bold'
      : 'text-on-surface-variant hover:bg-surface-container-high',
  ].join(' ');
}

/**
 * NavRail — 80px fixed navigation rail (md+) and a bottom navigation bar
 * on mobile. Brand mark on top, destinations center, avatar bottom.
 */
export default function NavRail() {
  const { user } = useAccount();

  return (
    <>
      <nav
        aria-label="Primary navigation"
        className="fixed left-0 top-0 z-50 hidden h-full w-[80px] flex-col items-center space-y-gap-section bg-surface py-gutter md:flex"
      >
        <div className="mb-gap-section mt-unit flex flex-col items-center">
          <NavLink to="/app/home" className="transition-transform active:scale-95" aria-label="Loaf Home">
            <BrandLogo className="h-10 w-10" />
          </NavLink>
        </div>

        <div className="flex w-full flex-col space-y-gap-stack px-2">
          {RAIL_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} className={({ isActive }) => railClass(isActive)}>
              {({ isActive }) => (
                <>
                  <Icon name={item.icon} fill={isActive} className="mb-1" />
                  <span className="font-label-sm text-label-sm">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="mt-auto flex flex-col items-center pb-gutter">
          <Avatar user={user} />
        </div>
      </nav>

      {/* Mobile bottom navigation */}
      <nav
        aria-label="Primary navigation"
        className="fixed bottom-0 left-0 z-50 flex min-h-[calc(4rem+env(safe-area-inset-bottom))] w-full items-center justify-around gap-1 border-t border-outline-variant/30 bg-surface px-2 pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        {RAIL_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              [
                'flex min-h-11 min-w-11 flex-1 flex-col items-center justify-center rounded-xl p-2 transition-colors',
                isActive
                  ? 'bg-primary text-on-primary font-semibold'
                  : 'text-on-surface-variant hover:bg-surface-container-high',
              ].join(' ')
            }
          >
            {({ isActive }) => (
              <>
                <Icon name={item.icon} fill={isActive} className="mb-1" />
                <span className="font-label-sm text-label-sm">{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </>
  );
}

function Avatar({ user }: { user: { name: string; picture?: string | null } | null }) {
  if (user?.picture) {
    return (
      <img
        src={user.picture}
        alt={user.name}
        className="h-10 w-10 rounded-full border border-outline-variant object-cover"
      />
    );
  }
  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full border border-outline-variant bg-surface-container-high font-title-md text-on-surface">
      {user ? initialsOf(user.name) : '·'}
    </div>
  );
}
